/*
 * For Base Theme
 * See https://github.com/chakra-ui/chakra-ui/blob/master/packages/theme/src/components/button.ts
 */

import { base, system } from '@workday/canvas-tokens-web'
import { Menu } from './Menu'

/**
 * Canvas Buttons mostly consume Canvas tokens directly. The remaining
 * --vndly-button-* fallbacks are component-scoped because legacy Buttons use
 * compact geometry, size-dependent type, distinct state colors, and composed
 * focus rings. Redefining broad primitives such as shape.full, base.size1200,
 * padding.xl, or fontWeight.bold would change unrelated components.
 *
 * LegacyGlobalCSSVariables defines these overrides only while
 * sana_rebrand_enabled is off, so removing the flag also removes this bridge.
 */
const cssVar = token => `var(${token})`
const legacyButtonFallback = (name, fallback) => `var(--vndly-button-${name}, ${fallback})`
const overlayBackground = overlay => `linear-gradient(${cssVar(overlay)}, ${cssVar(overlay)})`
const focusRing = `0 0 0 2px ${cssVar(system.color.surface.default)}, 0 0 0 4px ${cssVar(
  system.color.brand.focus.primary
)}`
const sizes = {
  sm: {
    height: '8',
    fontSize: 'sm'
  },
  md: {
    height: '10',
    fontSize: 'md'
  }
}
const actionButtonLayout = props => {
  if (props['data-vndly-icon-button'] !== undefined || !sizes[props.size]) return {}
  // min-height beats a shorter caller-defined height, so skip it when the caller sets one. A caller min height
  // already replaces the theme's and must not switch off height auto, or the button stops growing.
  const hasCallerHeight = ['h', 'height'].some(key => props[key] !== undefined || props.sx?.[key] !== undefined)
  return {
    ...(hasCallerHeight ? {} : { height: 'auto', minH: sizes[props.size].height }),
    // Chakra's fixed sm min width lets a crowded flex row squeeze the label below its longest word. md keeps
    // its Canvas min width, which min-content would drop for short labels.
    ...(props.size === 'sm' ? { minW: 'min-content' } : {}),
    whiteSpace: 'normal',
    textAlign: 'center',
    overflowWrap: 'break-word'
  }
}

const iconButtonMinWidth = props => {
  if (props['data-vndly-icon-button'] === undefined) return undefined
  if (props.size === 'sm') return '8'
  if (props.size === undefined || props.size === 'md') return '10'
  return undefined
}
const promotedButtonStyle = props => {
  const type = system.type.subtext.lg
  const minWidth = iconButtonMinWidth(props)

  return {
    borderRadius: legacyButtonFallback('border-radius', cssVar(system.shape.full)),
    fontFamily: cssVar(type.fontFamily),
    fontWeight: legacyButtonFallback('font-weight', cssVar(system.fontWeight.bold)),
    lineHeight: legacyButtonFallback(
      props.size === 'sm' ? 'small-line-height' : 'medium-line-height',
      cssVar(type.lineHeight)
    ),
    letterSpacing: cssVar(type.letterSpacing),
    ...(minWidth ? { minWidth } : {}),
    ...(!minWidth && props.size === 'md'
      ? {
          minWidth: legacyButtonFallback('medium-min-width', cssVar(base.size1200)),
          px: legacyButtonFallback('medium-padding-inline', cssVar(system.padding.xl))
        }
      : {})
  }
}

const baseStyle = props => {
  const minWidth = iconButtonMinWidth(props)

  return {
    borderRadius: legacyButtonFallback('border-radius', cssVar(system.shape.full)),
    fontSize: 'md',
    fontWeight: 'semibold',
    lineHeight: 'base',
    color: cssVar(system.color.fg.inverse),
    ...(minWidth ? { minWidth } : {}),
    _disabled: {
      opacity: '0.4'
    },
    '.chakra-spinner': {
      borderTopColor: legacyButtonFallback('spinner-color', 'currentColor'),
      borderRightColor: legacyButtonFallback('spinner-color', 'currentColor')
    },
    '> span > svg': {
      w: '16px'
    },
    '> svg': {
      w: '24px'
    }
  }
}

function variantPrimary(props) {
  return {
    ...props.theme.components.Button.variants.solid({ ...props }),
    ...promotedButtonStyle(props),
    ...actionButtonLayout(props),
    bgColor: cssVar(system.color.brand.accent.primary),
    color: cssVar(system.color.fg.inverse),
    _hover: {
      background: legacyButtonFallback('state-background-image', overlayBackground(system.color.accent.overlay.hover)),
      bgColor: legacyButtonFallback('background-primary-hover', cssVar(system.color.brand.accent.primary)),
      color: cssVar(system.color.fg.inverse),
      _disabled: {
        background: 'none',
        bgColor: cssVar(system.color.brand.accent.primary)
      }
    },
    _focusVisible: {
      bgColor: legacyButtonFallback('primary-focus-background', cssVar(system.color.brand.accent.primary)),
      boxShadow: legacyButtonFallback('focus-ring-middle', focusRing)
    },
    _active: {
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.accent.overlay.pressed)
      ),
      bgColor: legacyButtonFallback('background-primary-active', cssVar(system.color.brand.accent.primary))
    },
    _disabled: {
      bgColor: cssVar(system.color.brand.accent.primary)
    }
  }
}

function variantSecondary(props) {
  return {
    ...props.theme.components.Button.variants.outline({ ...props }),
    ...promotedButtonStyle(props),
    ...actionButtonLayout(props),
    bgColor: legacyButtonFallback('background-secondary', cssVar(system.color.surface.transparent)),
    borderWidth: '1px',
    borderColor: cssVar(system.color.border.input.default),
    color: legacyButtonFallback('secondary', cssVar(system.color.fg.default)),
    _hover: {
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.surface.overlay.hover.default)
      ),
      bgColor: legacyButtonFallback('background-secondary-hover', cssVar(system.color.surface.transparent)),
      borderColor: legacyButtonFallback('border-secondary-hover', cssVar(system.color.border.input.hover)),
      color: legacyButtonFallback('secondary-hover', cssVar(system.color.fg.strong)),
      _disabled: {
        background: 'none',
        bgColor: legacyButtonFallback('background-secondary', cssVar(system.color.surface.transparent)),
        borderColor: cssVar(system.color.border.input.default),
        color: legacyButtonFallback('secondary', cssVar(system.color.fg.default))
      }
    },
    _focusVisible: {
      color: legacyButtonFallback('secondary', cssVar(system.color.fg.strong)),
      bgColor: legacyButtonFallback('background-secondary', cssVar(system.color.surface.default)),
      borderColor: cssVar(system.color.border.input.default),
      boxShadow: legacyButtonFallback('focus-ring-outer', focusRing)
    },
    _active: {
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.surface.overlay.pressed.default)
      ),
      bgColor: legacyButtonFallback('background-secondary-active', cssVar(system.color.surface.transparent)),
      borderColor: legacyButtonFallback('border-secondary-hover', cssVar(system.color.border.input.hover)),
      color: legacyButtonFallback('secondary-hover', cssVar(system.color.fg.strong))
    }
  }
}

function variantDeletePrimary(props) {
  return {
    ...props.theme.components.Button.variants.solid({ ...props }),
    ...promotedButtonStyle(props),
    ...actionButtonLayout(props),
    bgColor: cssVar(system.color.brand.accent.critical),
    color: cssVar(system.color.fg.inverse),
    _hover: {
      background: legacyButtonFallback('state-background-image', overlayBackground(system.color.accent.overlay.hover)),
      bgColor: legacyButtonFallback('background-delete-primary-hover', cssVar(system.color.brand.accent.critical)),
      _disabled: {
        background: 'none',
        bgColor: cssVar(system.color.brand.accent.critical)
      }
    },
    _focusVisible: {
      bgColor: cssVar(system.color.brand.accent.critical),
      boxShadow: legacyButtonFallback('focus-ring-middle', focusRing)
    },
    _active: {
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.accent.overlay.pressed)
      ),
      bgColor: legacyButtonFallback('background-delete-primary-active', cssVar(system.color.brand.accent.critical))
    },
    _disabled: {
      background: cssVar(system.color.brand.accent.critical)
    }
  }
}

function variantDeleteSecondary(props) {
  return {
    ...props.theme.components.Button.variants.outline({ ...props }),
    ...promotedButtonStyle(props),
    ...actionButtonLayout(props),
    bgColor: legacyButtonFallback('background-delete-secondary', cssVar(system.color.surface.transparent)),
    borderWidth: '1px',
    borderColor: legacyButtonFallback('border-delete-secondary', cssVar(system.color.brand.border.critical)),
    color: cssVar(system.color.brand.fg.critical.default),
    _hover: {
      background: legacyButtonFallback('state-background-image', overlayBackground(system.color.accent.overlay.hover)),
      bgColor: legacyButtonFallback('background-delete-secondary-hover', cssVar(system.color.brand.accent.critical)),
      borderColor: legacyButtonFallback('border-delete-secondary-hover', cssVar(system.color.brand.border.critical)),
      color: cssVar(system.color.fg.inverse),
      _disabled: {
        background: 'none',
        bgColor: legacyButtonFallback('background-delete-secondary', cssVar(system.color.surface.transparent)),
        color: cssVar(system.color.brand.fg.critical.default)
      }
    },
    _focusVisible: {
      color: cssVar(system.color.brand.fg.critical.default),
      bgColor: legacyButtonFallback('background-delete-secondary', cssVar(system.color.surface.default)),
      borderColor: legacyButtonFallback('border-delete-secondary', cssVar(system.color.brand.border.critical)),
      boxShadow: legacyButtonFallback('focus-ring-outer', focusRing)
    },
    _active: {
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.accent.overlay.pressed)
      ),
      bgColor: legacyButtonFallback('background-delete-secondary-active', cssVar(system.color.brand.accent.critical)),
      borderColor: legacyButtonFallback('border-delete-secondary', cssVar(system.color.brand.border.critical)),
      color: cssVar(system.color.fg.inverse)
    }
  }
}

/**
 * Also referred as the Tertiary Button
 */
function variantLink(props) {
  const minWidth = iconButtonMinWidth(props)

  return {
    ...promotedButtonStyle(props),
    bgColor: cssVar(system.color.surface.transparent),
    color: cssVar(system.color.brand.fg.primary.default),
    height: props.size === 'md' ? '10' : '8',
    ...(!minWidth && props.size === 'md'
      ? {
          minWidth: legacyButtonFallback('tertiary-medium-min-width', 'auto'),
          p: `0 ${legacyButtonFallback('tertiary-medium-padding-inline', cssVar(system.padding.xs))}`
        }
      : {}),
    ...(!minWidth && props.size !== 'md' ? { p: '0 4' } : {}),
    _hover: {
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.surface.overlay.hover.default)
      ),
      bgColor: legacyButtonFallback('tertiary-background-hover', cssVar(system.color.surface.transparent)),
      color: cssVar(system.color.brand.fg.primary.strong),
      textDecoration: 'underline'
    },
    _focusVisible: {
      bgColor: cssVar(system.color.surface.transparent),
      color: legacyButtonFallback('tertiary-focus-color', cssVar(system.color.brand.fg.primary.default)),
      boxShadow: legacyButtonFallback('focus-ring-middle', focusRing),
      textDecoration: 'underline'
    },
    _active: {
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.surface.overlay.pressed.default)
      ),
      bgColor: legacyButtonFallback('tertiary-background-active', cssVar(system.color.surface.transparent)),
      color: cssVar(system.color.brand.fg.primary.strong),
      textDecoration: 'underline'
    }
  }
}

/**
 * @deprecated We are no longer using button variant `linkDelete`. Instead we have
 * introduced a primary (deletePrimary) and secondary (deleteSecondary) destructive
 * button variants respectively.
 */
function variantLinkDelete(props) {
  return {
    bgColor: cssVar(system.color.surface.transparent),
    fontWeight: 'var(--vndly-font-weight-semibold)',
    lineHeight: 'base',
    color: 'var(--vndly-color-link-delete)',
    height: props.size === 'md' ? '10' : '8',
    p: '0 4',
    _hover: {
      color: 'var(--vndly-color-link-delete-hover)',
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.surface.overlay.hover.default)
      ),
      bgColor: legacyButtonFallback('tertiary-background-hover', cssVar(system.color.surface.transparent)),
      textDecoration: 'underline'
    },
    _focusVisible: {
      color: 'var(--vndly-color-link-delete)',
      boxShadow: 'middleOutline',
      textDecoration: 'underline'
    },
    _active: {
      color: 'var(--vndly-color-link-delete-hover)',
      background: legacyButtonFallback(
        'state-background-image',
        overlayBackground(system.color.surface.overlay.hover.default)
      ),
      bgColor: legacyButtonFallback('tertiary-background-hover', cssVar(system.color.surface.transparent)),
      textDecoration: 'underline'
    }
  }
}

const variantLinkInlineBlue = {
  bgColor: 'var(--vndly-color-link-background)',
  fontSize: 'var(--vndly-font-size-md)',
  fontWeight: 'var(--vndly-font-weight-semibold)',
  lineHeight: 'base',
  height: 'auto',
  color: 'var(--vndly-color-link)',
  padding: 0,
  textDecoration: 'underline',
  _hover: {
    color: 'var(--vndly-color-link-hover)',
    bgColor: 'var(--vndly-color-link-background-hover)',
    textDecoration: 'underline'
  },
  _focusVisible: {
    boxShadow: 'middleOutline'
  },
  _active: {
    color: 'var(--vndly-color-link-hover)'
  }
}

const variantLinkInlineGray = {
  padding: 0,
  height: 'auto',
  bgColor: 'transparent',
  fontSize: 'md',
  fontWeight: 'semibold',
  lineHeight: 'base',
  color: 'gray.500',
  textDecoration: 'underline',
  _hover: {
    color: 'gray.600',
    textDecoration: 'none'
  }
}

/**
 * @deprecated We are no longer supporting inlineDark Button variant in the Design System.
 * Alternatively, use variant `inlineBlue` (ie. blue link) or `inlineGray` (ie. subdued link)
 */
const variantLinkInlineDark = {
  padding: 0,
  height: 'auto',
  bgColor: 'transparent',
  fontSize: 'md',
  fontWeight: 'semibold',
  lineHeight: 'base',
  color: 'gray.800',
  textDecoration: 'underline',
  _hover: {
    color: 'gray.900',
    textDecoration: 'none'
  }
}

const variantGhost = {
  _hover: {
    bgColor: 'gray.200'
  }
}

const variantQuickAction = {
  cursor: 'pointer',
  color: legacyButtonFallback('quick-action-color', cssVar(system.color.fg.default)),
  bgColor: legacyButtonFallback('quick-action-background', cssVar(system.color.surface.default)),
  border: '1px solid',
  borderColor: legacyButtonFallback('quick-action-border', cssVar(system.color.border.input.default)),
  borderRadius: legacyButtonFallback('border-radius', cssVar(system.shape.full)),
  boxShadow: legacyButtonFallback('quick-action-shadow', 'none'),
  w: '100%',
  minHeight: '88px',
  whiteSpace: 'normal',
  justifyContent: 'flex-start'
}

const variantPager = {
  color: 'gray.800',
  fontWeight: 'normal',
  _hover: {
    bgColor: 'gray.200'
  }
}

const variantList = {
  cursor: 'pointer',
  borderRadius: '0',
  borderLeft: '4px solid',
  borderLeftColor: 'transparent',
  fontSize: 'md',
  fontWeight: 'normal',
  lineHeight: 'base',
  color: 'gray.800',
  justifyContent: 'flex-start',
  pl: '4',
  _hover: {
    bgColor: 'gray.50'
  },
  _active: {
    bgColor: 'gray.50',
    fontWeight: 'semibold',
    borderLeftColor: 'blue.500'
  },
  _checked: {
    bgColor: 'gray.50',
    fontWeight: 'semibold',
    borderLeftColor: 'blue.500'
  },
  _focusVisible: {
    boxShadow: 'insetOutline'
  }
}

const variantMobileMenu = {
  ...Menu.variants.mainMenu.button,
  w: 'full',
  rounded: 0,
  justifyContent: 'flex-start',
  h: '3rem',
  '.mobile-button__chevron': {
    color: 'system.color.fg.muted.soft'
  },
  _hover: {
    ...Menu.variants.mainMenu.button._hover,
    '.mobile-button__chevron': {
      color: 'system.color.fg.muted.stronger'
    }
  },
  '.menu-button__icon': {
    ...Menu.variants.mainMenu.button['.menu-button__icon'],
    mr: 2
  }
}

const variantViewAll = {
  w: 'full',
  height: '24px',
  fontSize: 'sm',
  fontWeight: 'normal',
  lineHeight: 'base',
  color: 'gray.500',
  bgColor: 'gray.100',
  borderRadius: 'none',
  mt: 4
}

export const Button = {
  baseStyle,
  variants: {
    primary: variantPrimary,
    secondary: variantSecondary,
    deletePrimary: variantDeletePrimary,
    deleteSecondary: variantDeleteSecondary,
    link: variantLink, // Deprecating: Use variant tertiary instead of variant link for Link Buttons. The naming will align with Canvas' button types.
    tertiary: variantLink,
    linkDelete: variantLinkDelete,
    inlineLink: variantLinkInlineBlue,
    inlineGray: variantLinkInlineGray,
    inlineDark: variantLinkInlineDark,
    ghost: variantGhost,
    quickAction: variantQuickAction,
    pager: variantPager,
    list: variantList,
    mainMenu: Menu.variants.mainMenu.button,
    mobileMenu: variantMobileMenu,
    profileMenu: Menu.variants.profileMenu.button,
    viewAll: variantViewAll
  },
  sizes,
  defaultProps: {
    variant: 'secondary',
    size: 'md'
  }
}
